import { useState } from "react";
import { useAdminSubscriptions } from "@/lib/api/subscriptions.queries";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Table, THead, TH, TBody, TR, TD, EmptyState } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { SubscriptionStatusBadge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";

export function SubscriptionsList() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [statusFilter, setStatusFilter] = useState("");

  const query = useAdminSubscriptions({
    status: statusFilter || undefined,
    page,
    pageSize,
  });

  const total = query.data?.pagination.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Suscripciones</h1>
        <p className="text-sm text-slate-500 mt-1">
          Estado de facturación de todos los equipos de la plataforma.
        </p>
      </div>

      <div className="flex items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <option value="">Todos los estados</option>
          <option value="active">Activo</option>
          <option value="trialing">Periodo de prueba</option>
          <option value="past_due">Vencido</option>
          <option value="cancelled">Cancelado</option>
        </select>
      </div>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Cargando suscripciones…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : (query.data?.subscriptions ?? []).length === 0 ? (
          <EmptyState title="Sin suscripciones" description="No hay suscripciones con el estado seleccionado." />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>ID</TH>
                  <TH>Equipo</TH>
                  <TH>Plan</TH>
                  <TH>Estado</TH>
                  <TH>Período actual</TH>
                  <TH>Último pago</TH>
                  <TH>Próximo cobro</TH>
                </tr>
              </THead>
              <TBody>
                {query.data.subscriptions.map((s) => (
                  <TR key={s.id}>
                    <TD className="font-mono text-[11px]">{s.id}</TD>
                    <TD>
                      <p className="font-medium text-slate-900">{s.teamName ?? s.teamId}</p>
                      <p className="text-[11px] font-mono text-slate-500">{s.teamId}</p>
                    </TD>
                    <TD className="text-xs capitalize">{s.plan}</TD>
                    <TD><SubscriptionStatusBadge status={s.status} /></TD>
                    <TD className="text-[11px] text-slate-500">
                      {s.currentPeriodEnd ? formatDate(s.currentPeriodEnd) : "—"}
                    </TD>
                    <TD className="text-[11px] text-slate-500">
                      {s.lastPaymentAt ? formatDate(s.lastPaymentAt) : "—"}
                    </TD>
                    <TD className="text-[11px] text-slate-500">
                      {s.nextDunningAt ? formatDate(s.nextDunningAt) : "—"}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">
                {total} total · página {page} de {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Anterior
                </Button>
                <Button variant="outline" size="sm" disabled={page >= pageCount} onClick={() => setPage((p) => p + 1)}>
                  Siguiente
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
